import React, { useState, useRef } from 'react';
import {
  Menu, MenuButton, MenuList, MenuItem,
  Box, Button, Flex, Text, HStack, IconButton, Badge, Select, SimpleGrid,
  useColorModeValue, Icon, Portal
} from '@chakra-ui/react';
import { ViewIcon, ExternalLinkIcon, EditIcon, DeleteIcon } from '@chakra-ui/icons';
import ConfirmationModal from './ConfirmationModal';

const InvoiceList = ({ invoices, onEdit, onDelete, onViewDetails, onPrintInvoice, onChangeStatus, onPreloadDetails }) => {
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);

  // Move all useColorModeValue calls to the top level
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.800', 'white');
  const subtextColor = useColorModeValue('gray.600', 'gray.400');
  const menuBg = useColorModeValue('white', 'gray.700');
  const menuBorderColor = useColorModeValue('gray.200', 'gray.600');
  const blueHoverBg = useColorModeValue('blue.50', 'blue.900');
  const orangeHoverBg = useColorModeValue('orange.50', 'orange.900');
  const greenHoverBg = useColorModeValue('green.50', 'green.900');
  const purpleHoverBg = useColorModeValue('purple.50', 'purple.900');
  const redHoverBg = useColorModeValue('red.50', 'red.900');
  const blueColor = useColorModeValue('blue.600', 'blue.300');
  const purpleColor = useColorModeValue('purple.600', 'purple.300');
  const greenColor = useColorModeValue('green.600', 'green.300');
  const redColor = useColorModeValue('red.600', 'red.300');
  const blueBg = useColorModeValue('blue.50', 'blue.900');
  const purpleBg = useColorModeValue('purple.50', 'purple.900');
  const greenBg = useColorModeValue('green.50', 'green.900');
  const redBg = useColorModeValue('red.50', 'red.900');
  const blueHoverColor = useColorModeValue('blue.100', 'blue.800');
  const purpleHoverColor = useColorModeValue('purple.100', 'purple.800');
  const greenHoverColor = useColorModeValue('green.100', 'green.800');
  const redHoverColor = useColorModeValue('red.100', 'red.800');
  const redHeaderBg = useColorModeValue('red.50', 'red.900');
  const alertTextColor = useColorModeValue('gray.700', 'gray.300');
  const grayBg = useColorModeValue('gray.100', 'gray.600');
  const grayColor = useColorModeValue('gray.700', 'white');
  const grayHoverBg = useColorModeValue('gray.200', 'gray.500');
  
  // Additional color values for customer and totals sections
  const customerBg = useColorModeValue('blue.50', 'blue.900');
  const customerBorderColor = useColorModeValue('blue.200', 'blue.700');
  const customerTextColor = useColorModeValue('blue.700', 'blue.300');

  const handleDeleteClick = (id) => {
    setSelectedInvoiceId(id);
    setIsConfirmModalOpen(true);
  };

  const confirmDelete = () => {
    if (selectedInvoiceId !== null) {
      onDelete(selectedInvoiceId);
      setSelectedInvoiceId(null);
      setIsConfirmModalOpen(false);
    }
  };

  return (
    <>
      <SimpleGrid columns={[1, 1, 2]} spacing={6}>
        {invoices.map((invoice) => (
          <Box 
            key={invoice.id} 
            position="relative"
            bg={cardBg}
            p={6} 
            borderRadius="xl" 
            boxShadow="0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)"
            border="1px solid"
            borderColor={borderColor}
            overflow="visible"
            zIndex={1}
            _hover={{
              transform: "translateY(-2px)",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
              transition: "all 0.2s ease",
              zIndex: 10
            }}
            transition="all 0.2s ease"
          >
            {/* Decorative gradient overlay - Dynamic based on status */}
            <Box
              position="absolute"
              top={0}
              left={0}
              right={0}
              height="4px"
              bg={
                invoice.status === 'completed' 
                  ? "linear-gradient(90deg, #10B981, #059669, #047857)" // Green for completed
                  : invoice.status === 'pending'
                    ? "linear-gradient(90deg, #F59E0B, #D97706, #B45309)" // Orange for pending
                    : "linear-gradient(90deg, #3B82F6, #2563EB, #1D4ED8)" // Blue for new
              }
              borderTopLeftRadius="xl"
              borderTopRightRadius="xl"
            />
            
            <Flex direction="column" h="100%">
              <Flex justify="space-between" align="flex-start" mb={4}>
                <Flex align="center">
                  {/* Status Icon */}
                  <Box
                    p={3}
                    bg={
                      invoice.status === 'completed' 
                        ? "linear-gradient(135deg, #10B981, #059669)"
                        : invoice.status === 'pending'
                          ? "linear-gradient(135deg, #F59E0B, #D97706)"
                          : "linear-gradient(135deg, #3B82F6, #2563EB)"
                    }
                    borderRadius="lg"
                    mr={4}
                    boxShadow={
                      invoice.status === 'completed' 
                        ? "0 4px 6px -1px rgba(16, 185, 129, 0.3)"
                        : invoice.status === 'pending'
                          ? "0 4px 6px -1px rgba(245, 158, 11, 0.3)"
                          : "0 4px 6px -1px rgba(59, 130, 246, 0.3)"
                    }
                  >
                    <Icon viewBox="0 0 24 24" boxSize={5} color="white">
                      <path fill="currentColor" d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
                    </Icon>
                  </Box>

                  <Box flex="1">
                    <Text 
                      fontWeight="bold" 
                      fontSize="lg"
                      color={textColor}
                      mb={1}
                    >
                      Накладна #{invoice.id}
                    </Text>
                    <Text 
                      fontSize="sm" 
                      color={subtextColor}
                      mb={2}
                    >
                      Дата створення: {new Date(invoice.created_at).toLocaleString()}
                    </Text>

                    <Menu strategy="fixed">
                      <MenuButton
                        as={Badge}
                        bg={
                          invoice.status === 'completed'
                            ? "linear-gradient(135deg, #10B981, #059669)"
                            : invoice.status === 'pending'
                              ? "linear-gradient(135deg, #F59E0B, #D97706)"
                              : "linear-gradient(135deg, #3B82F6, #2563EB)"
                        }
                        color="white"
                        px={3}
                        py={1}
                        borderRadius="full"
                        cursor="pointer"
                        _hover={{
                          transform: "scale(1.05)",
                          transition: "all 0.2s ease"
                        }}
                        transition="all 0.2s ease"
                      >
                        {invoice.status === 'new' ? 'новий' : invoice.status === 'pending' ? 'в очікуванні' : 'проведено'}
                      </MenuButton>
                      <Portal>
                        <MenuList
                          bg={menuBg}
                          borderColor={menuBorderColor}
                          boxShadow="0 10px 15px -3px rgba(0, 0, 0, 0.1)"
                          zIndex={9999}
                          minWidth="120px"
                        >
                        <MenuItem 
                          onClick={() => onChangeStatus(invoice.id, 'new')}
                          _hover={{ bg: blueHoverBg }}
                        >
                          Новий
                        </MenuItem>
                        <MenuItem 
                          onClick={() => onChangeStatus(invoice.id, 'pending')}
                          _hover={{ bg: orangeHoverBg }}
                        >
                          В очікуванні
                        </MenuItem>
                        <MenuItem 
                          onClick={() => onChangeStatus(invoice.id, 'completed')}
                          _hover={{ bg: greenHoverBg }}
                        >
                          Проведено
                        </MenuItem>
                        </MenuList>
                      </Portal>
                    </Menu>
                  </Box>
                </Flex>
              </Flex>

              {/* Customer and Totals Information Combined */}
              {(invoice.customer_name || invoice.supplier_name || invoice.total_without_vat || invoice.total_with_vat) && (
                <Box 
                  bg={customerBg}
                  p={4}
                  borderRadius="lg"
                  mb={4}
                  border="1px solid"
                  borderColor={customerBorderColor}
                >
                  {/* Customer/Supplier Information */}
                  {(invoice.customer_name || invoice.supplier_name) && (
                    <Box mb={3}>
                      <Text 
                        fontSize="sm" 
                        fontWeight="medium"
                        color={customerTextColor}
                        mb={1}
                      >
                        {invoice.supplier_name ? 'Постачальник:' : 'Клієнт:'}
                      </Text>
                      <Text 
                        fontSize="md"
                        color={textColor}
                        fontWeight="semibold"
                      >
                        {invoice.supplier_name || invoice.customer_name}
                      </Text>
                    </Box>
                  )}
                  
                  {/* Totals Information */}
                  {(invoice.total_without_vat || invoice.total_with_vat) && (
                    <Box>
                      <Text 
                        fontSize="sm" 
                        fontWeight="medium"
                        color={customerTextColor}
                        mb={2}
                      >
                        Підсумок:
                      </Text>
                      <Text 
                        fontSize="sm"
                        color={textColor}
                        mb={1}
                      >
                        Разом без ПДВ: <Text as="span" fontWeight="semibold">
                          {parseFloat(invoice.total_without_vat || 0).toFixed(2)} грн
                        </Text>
                      </Text>
                      <Text 
                        fontSize="sm"
                        color={textColor}
                        mb={1}
                      >
                        ПДВ: <Text as="span" fontWeight="semibold">
                          {parseFloat(invoice.vat || 0).toFixed(2)} грн
                        </Text>
                      </Text>
                      <Text 
                        fontSize="md"
                        color={customerTextColor}
                        fontWeight="bold"
                      >
                        Всього з ПДВ: {parseFloat(invoice.total_with_vat || 0).toFixed(2)} грн
                      </Text>
                    </Box>
                  )}
                </Box>
              )}

              {/* Action Buttons */}
              <Box mt="auto">
                <HStack spacing={2} justify="center">
                  <IconButton 
                    icon={<ViewIcon />} 
                    size="sm" 
                    onClick={() => onViewDetails(invoice.id)} 
                    onMouseEnter={() => onPreloadDetails && onPreloadDetails(invoice.id)}
                    aria-label="View"
                    bg={blueBg}
                    color={blueColor}
                    _hover={{
                      bg: blueHoverColor,
                      transform: "translateY(-1px)",
                      boxShadow: "0 4px 6px -1px rgba(59, 130, 246, 0.3)"
                    }}
                    transition="all 0.2s ease"
                    borderRadius="lg"
                  />
                  <IconButton 
                    icon={<ExternalLinkIcon />} 
                    size="sm" 
                    onClick={() => onPrintInvoice(invoice.id)} 
                    aria-label="Print"
                    bg={purpleBg}
                    color={purpleColor}
                    _hover={{
                      bg: purpleHoverColor,
                      transform: "translateY(-1px)",
                      boxShadow: "0 4px 6px -1px rgba(139, 92, 246, 0.3)"
                    }}
                    transition="all 0.2s ease"
                    borderRadius="lg"
                  />
                  <IconButton 
                    icon={<EditIcon />} 
                    size="sm" 
                    onClick={() => onEdit(invoice.id)} 
                    aria-label="Edit" 
                    isDisabled={invoice.status === 'completed'}
                    bg={greenBg}
                    color={greenColor}
                    _hover={{
                      bg: greenHoverColor,
                      transform: "translateY(-1px)",
                      boxShadow: "0 4px 6px -1px rgba(16, 185, 129, 0.3)"
                    }}
                    _disabled={{
                      opacity: 0.5,
                      cursor: "not-allowed",
                      _hover: {}
                    }}
                    transition="all 0.2s ease"
                    borderRadius="lg"
                  />
                  <IconButton 
                    icon={<DeleteIcon />} 
                    size="sm" 
                    colorScheme="red" 
                    onClick={() => handleDeleteClick(invoice.id)} 
                    aria-label="Delete"
                    bg={redBg}
                    color={redColor}
                    _hover={{
                      bg: redHoverColor,
                      transform: "translateY(-1px)",
                      boxShadow: "0 4px 6px -1px rgba(239, 68, 68, 0.3)"
                    }}
                    transition="all 0.2s ease"
                    borderRadius="lg"
                  />
                </HStack>
              </Box>
            </Flex>
          </Box>
        ))}
      </SimpleGrid>

      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={confirmDelete}
        message="Ви впевнені, що хочете видалити цю накладну? Цю дію не можна буде скасувати."
      />
    </>
  );
};

export default InvoiceList;
